import React, { useState } from "react";
import useCoinStore from "../stores/coinStore";
import {
  CLASSIC_CATEGORIES,
  FREE_CATEGORY_COUNT,
  MAX_CATEGORIES,
  MIN_CATEGORIES,
  MAX_CATEGORY_LENGTH,
  getAddCategoryCost,
  normalizeCategory,
  validateCategories,
} from "./Utils/categoryRules";

const CategoryManager = ({
  currentCategories,
  onCategoriesChange,
  roomId,
  onClose,
  onBuyCoins,
}) => {
  const initial = currentCategories?.length ? currentCategories : CLASSIC_CATEGORIES;
  const [rows, setRows] = useState([...initial]);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [confirmingCost, setConfirmingCost] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const { coinBalance, canAfford, spendCoins, addCoins } = useCoinStore();

  // Slots the room already paid for stay free to refill during this edit.
  // Coins spent in this session are only real once Save succeeds, so they're
  // tracked separately and refunded on cancel or if left unused.
  const savedSlots = Math.max(FREE_CATEGORY_COUNT, initial.length);
  const [purchases, setPurchases] = useState([]);
  const openSlots = savedSlots + purchases.length;
  const addCost = rows.length < openSlots ? 0 : getAddCategoryCost(rows.length);
  const lostSavedSlots = Math.max(0, savedSlots - Math.max(rows.length, FREE_CATEGORY_COUNT));

  const refund = (amounts, purpose) => {
    const total = amounts.reduce((sum, n) => sum + n, 0);
    if (!total) return;
    addCoins(total, { purpose, roomId }).catch((err) =>
      console.error("Failed to refund extra categories:", err)
    );
  };

  const updateRow = (index, value) => {
    setRows(rows.map((r, i) => (i === index ? value : r)));
    setError("");
  };

  const removeRow = (index) => {
    if (rows.length <= MIN_CATEGORIES) {
      setError(`A game needs at least ${MIN_CATEGORIES} categories.`);
      return;
    }
    setRows(rows.filter((_, i) => i !== index));
    setError("");
  };

  const addCategory = async () => {
    const name = normalizeCategory(newName);
    if (!name) return;
    if (rows.some((r) => normalizeCategory(r) === name)) {
      setError(`"${name}" is already in the list.`);
      return;
    }

    if (addCost > 0) {
      if (!confirmingCost) {
        setConfirmingCost(addCost);
        return;
      }
      setProcessing(true);
      const result = await spendCoins(addCost, "extra_category", roomId);
      setProcessing(false);
      setConfirmingCost(0);
      if (!result.success) {
        setError(result.message || "Couldn't add the category. Please try again.");
        return;
      }
      setPurchases([...purchases, addCost]);
    }

    setRows([...rows, name]);
    setNewName("");
    setAdding(false);
    setError("");
  };

  const closeAdd = () => {
    setAdding(false);
    setNewName("");
    setConfirmingCost(0);
  };

  const handleSave = async () => {
    const problem = validateCategories(rows);
    if (problem) {
      setError(problem);
      return;
    }

    setSaving(true);
    setError("");
    const result = await onCategoriesChange(rows.map(normalizeCategory));
    setSaving(false);

    if (result === false) {
      setError("Couldn't save. Please try again.");
      return;
    }

    const unusedPurchases = Math.min(purchases.length, openSlots - Math.max(rows.length, savedSlots));
    if (unusedPurchases > 0) {
      refund(purchases.slice(-unusedPurchases), "extra_category_refund");
    }
    onClose();
  };

  const handleCancel = () => {
    refund(purchases, "extra_category_refund");
    onClose();
  };

  const canAddMore = rows.length < MAX_CATEGORIES;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[65] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-5">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Categories</h2>
              <p className="text-sm text-gray-500 mt-1">Tap a category to rename it.</p>
            </div>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600 transition-colors p-2 -mr-2 rounded-full hover:bg-gray-100"
              title="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-2">
            {rows.map((category, index) => (
              <div key={index} className="flex items-center gap-1">
                <label className="relative flex-1 min-w-0">
                  <input
                    type="text"
                    value={category}
                    maxLength={MAX_CATEGORY_LENGTH}
                    onChange={(e) => updateRow(index, e.target.value)}
                    placeholder="Category name"
                    className="w-full bg-gray-50 border border-gray-200 rounded-lg pl-3 pr-9 py-2.5 capitalize font-medium text-gray-800 focus:bg-white focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                  <svg
                    className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M4 20h4L18.768 9.232a2.5 2.5 0 00-3.536-3.536L4 16.464V20z" />
                  </svg>
                </label>
                {rows.length > MIN_CATEGORIES && (
                  <button
                    onClick={() => removeRow(index)}
                    className="p-2 text-gray-300 hover:text-red-600 rounded-lg"
                    title={`Remove ${category || "category"}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>

          {canAddMore && !adding && (
            <button
              onClick={() => setAdding(true)}
              className="mt-3 w-full py-2.5 rounded-lg border border-dashed border-gray-300 text-sm font-semibold text-gray-600 hover:border-red-400 hover:text-red-600"
            >
              + Add category{addCost > 0 && ` · ${addCost} coins`}
            </button>
          )}

          {canAddMore && adding && (
            <div className="mt-3 rounded-lg border border-gray-200 p-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  autoFocus
                  value={newName}
                  maxLength={MAX_CATEGORY_LENGTH}
                  onChange={(e) => {
                    setNewName(e.target.value);
                    setConfirmingCost(0);
                  }}
                  onKeyDown={(e) => e.key === "Enter" && addCategory()}
                  placeholder="e.g. Movies"
                  className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
                <button
                  onClick={addCategory}
                  disabled={!newName.trim() || processing || (addCost > 0 && !canAfford(addCost))}
                  className="px-4 py-2 text-sm rounded-lg font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {processing ? "Adding..." : confirmingCost ? `Pay ${confirmingCost}` : "Add"}
                </button>
              </div>
              <div className="mt-2 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-gray-500">
                <span>
                  {addCost === 0
                    ? "Free"
                    : confirmingCost
                    ? `Tap Pay to add it for ${confirmingCost} coins.`
                    : `Costs ${addCost} coins · you have ${coinBalance}`}
                  {addCost > 0 && !canAfford(addCost) && onBuyCoins && (
                    <>
                      {" · "}
                      <button onClick={onBuyCoins} className="font-semibold text-red-600 hover:underline">
                        Get coins
                      </button>
                    </>
                  )}
                </span>
                <button onClick={closeAdd} className="text-gray-500 hover:text-gray-700">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {!canAddMore && (
            <p className="mt-3 text-xs text-gray-500 text-center">That's the most categories a game can have.</p>
          )}

          {lostSavedSlots > 0 && (
            <p className="mt-3 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
              You paid for {lostSavedSlots === 1 ? "the category you removed" : "the categories you removed"}. If you
              save now, adding more later costs coins again.
            </p>
          )}

          {error && <p className="mt-3 text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2">{error}</p>}

          <button
            onClick={handleSave}
            disabled={saving}
            className="mt-6 w-full py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold disabled:opacity-60"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CategoryManager;
